// Страница предмета /{ru|az|en}/tutors/{subject} (16.10.2026, SEO фаза 2):
// H1, короткий лид, карточки анкет с этим предметом (рисуются сервером из
// loader), ссылка на карту с фильтром и соседние предметы. Есть только у
// предметов с ≥ 3 анкетами — правило в src/lib/tutor-subject-pages.ts.
import { MAP_COPY, TUTORS_PATH } from "./map-copy";
import { mapQueryHref } from "./map-href";
import { PartnerCard } from "./TutorsMapPage";
import { SiteFooter, SiteHeader, type SiteLang } from "@/components/site/SiteChrome";
import type { PublicPartner } from "@/lib/map-public.functions";
import { neighbourSubjects, subjectLabel, subjectPageText, subjectPath } from "@/lib/tutor-subject-pages";
import { careerLinks, careersForSubject } from "@/lib/seo-links";

export type SubjectLoad = { code: string; rows: PublicPartner[]; pageCodes: string[] };

const HOME: Record<SiteLang, string> = { ru: "/ru", az: "/az", en: "/en" };

const SUBJECT_CAREERS_TITLE: Record<SiteLang, string> = { az: "Bu fənn hansı peşələrdə lazımdır", ru: "В каких профессиях нужен этот предмет", en: "Careers where this subject matters" };

export function TutorSubjectPage({ lang, subject }: { lang: SiteLang; subject: SubjectLoad }) {
  const c = MAP_COPY[lang];
  const { code, rows, pageCodes } = subject;
  const text = subjectPageText(code, lang, rows.length);
  const langHref = { ru: subjectPath(code, "ru"), az: subjectPath(code, "az"), en: subjectPath(code, "en") };
  const neighbours = neighbourSubjects(code, pageCodes);
  // Профессии, где предмет нужен (29.10.2026) — только очевидные связи, 3–5 ссылок.
  const careers = careerLinks(careersForSubject(code), lang).slice(0, 5);
  return (
    <div className="hp" lang={lang}>
      <SiteHeader lang={lang} active="tutors" langHref={langHref} />
      <main className="tm tm-subject">
        <div className="tj-wrap">
          <nav className="tm-crumbs" aria-label="breadcrumb">
            <a href={HOME[lang]}>Yolutap</a>
            <span aria-hidden="true"> / </span>
            <a href={TUTORS_PATH[lang]}>{c.breadcrumbHub}</a>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">{subjectLabel(code, lang)}</span>
          </nav>
          <div className="tm-head">
            <div className="tm-head__title">
              <h1>{text.h1}</h1>
              <span className="tm-muted">{text.lead}</span>
            </div>
            <a className="hp-btn" href={mapQueryHref(TUTORS_PATH[lang], { layer: "tutor", subject: code })}>
              {c.subjectOnMap}
            </a>
          </div>
          <section className="tm-subject__list">
            {rows.map((p) => (
              <PartnerCard key={p.id} p={p} lang={lang} />
            ))}
          </section>
          {neighbours.length > 0 && (
            <nav className="tm-about" aria-label={c.subjectNeighbours}>
              <h2>{c.subjectNeighbours}</h2>
              <ul className="tm-about__subjects">
                {neighbours.map((n) => (
                  <li key={n}>
                    <a href={subjectPath(n, lang)}>{subjectLabel(n, lang)}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {careers.length > 0 && (
            <nav className="tm-about" aria-label={SUBJECT_CAREERS_TITLE[lang]} data-subject-careers="">
              <h2>{SUBJECT_CAREERS_TITLE[lang]}</h2>
              <ul className="tm-about__subjects">
                {careers.map((x) => (
                  <li key={x.id}>
                    <a href={x.href}>{x.name}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </main>
      <SiteFooter lang={lang} active="tutors" langHref={langHref} />
    </div>
  );
}
